import { useContext, useEffect } from "react"
import { useShallow } from "zustand/react/shallow"

import { FinalExamination } from "./ui"
import { IFinalExamination } from "./types"
import { IFinalExaminationList } from "@api/finalExamination/state.types"

import { useMain } from "@api/main/state"
import { useFinalExamination } from "@api/finalExamination/state"

import { useValue } from "@hooks/useValue"
import { useDebounce } from "@hooks/useDebounce"

import { IModalConfirm, ModalConfirm } from "@primitive/modal.confirm"

import { CardContractContainer, ICardContractContainer } from "@smart/cardContract"
import { CardListenerContainer, ICardListenerContainer } from "@smart/cardListener"
import { CardFinalExaminationContainer, ICardFinalExaminationContainer } from "@smart/cardFinalExamination"
import { FinalExaminationJobsContainer, IFinalExaminationJobsContainer } from "@smart/finalExaminationJobs"

import { NotificationContext, notifyOpen } from "@ui/notification/notification.provider"


/** Итоговые работы - Контейнер */
export const FinalExaminationContainer = () => {
    const notify = useContext(NotificationContext)

    const cardFinalExamination = useValue<ICardFinalExaminationContainer>({isOpen: false})
    const finalExaminationJobs = useValue<IFinalExaminationJobsContainer>({isOpen: false})
    const cardContract = useValue<ICardContractContainer>({isOpen: false})
    const cardListener = useValue<ICardListenerContainer>({isOpen: false})

    const loadingTitle = useValue<string>('Загрузка итоговых работ...')
    const modalConfirm = useValue<IModalConfirm>({isOpen: false})

    const currentPageNumber = useValue<number>(1)
    const txtSearch = useValue<string>('')

    const finalExaminationState = useFinalExamination(useShallow((state) => state))

    // Хранилище страницы - Главная
    const { countUnverifiedWorks } = useMain(useShallow((state) => ({
        countUnverifiedWorks: state.badge.countUnverifiedWorks,
    })))

    useEffect(() => {
        return (() => {
            finalExaminationState.clearFinalExaminationState()

            currentPageNumber.setValue(1)
            txtSearch.setValue('')        
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect(() => {
        if (finalExaminationState.error) return notify && notifyOpen(finalExaminationState.error?.message, finalExaminationState.error?.type, 3000, notify)
    }, [finalExaminationState.error]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadDataListeners(txtSearch.value)
    }, [currentPageNumber.value]) // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect(() => {
        finalExaminationState.isReloadListeners && loadDataListeners(txtSearch.value)
    }, [finalExaminationState.isReloadListeners]) // eslint-disable-line react-hooks/exhaustive-deps

    /** Функция по получению списка слушателей */
    const loadDataListeners = (search?: string) => {
        finalExaminationState.actionGetFinalExaminationListeners(currentPageNumber.value, search || '')
    }



        /** Открытие/закрытие окна подтверждения */
    const handlerOpenModalConfirm = (isOpen: boolean, headerText: string, contentText: string, actionName?: string, recordID?: number) => {
        modalConfirm.setValue({
            isOpen, headerText, contentText, 
            handleButtonCancel() {modalConfirm.setValue({isOpen: false})}, 
            handleButtonOk() {
                switch (actionName) {
                    case 'inArchive': {
                        loadingTitle.setValue('Отправляю в архив...')

                        finalExaminationState.actionSetFinalExaminationInArchive(Number(recordID)).then((res) => {
                            if (res === 200) {
                                loadDataListeners(txtSearch.value)
                                return notify && notifyOpen('Анкета итоговой работы слушателя, успешно отправлена в архив.', 'success', 1500, notify)
                            }
                        }).finally(() => { loadingTitle.setValue('Загрузка итоговых работ...') })
                        break
                    } // Создание и получение нового номера по договору

                    case 'toPrint': {
                        loadingTitle.setValue('Отправляю в печать...')

                        finalExaminationState.actionSetFinalExaminationToPrint(Number(recordID)).then((res) => {
                            if (res === 200) {
                                loadDataListeners(txtSearch.value)
                                return notify && notifyOpen('Анкета итоговой работы слушателя, успешно отправлена в печать.', 'success', 1500, notify)
                            }
                        }).finally(() => { loadingTitle.setValue('Загрузка итоговых работ...') })
                        break
                    } // Допуск слушателя к обучению
                }

                modalConfirm.setValue({isOpen: false})
            }
        })
    }

    /** Отправка в архив всех итоговых работ в группе недели */
    const handleArchiveWeek = (weekLabel: string, items: IFinalExaminationList[]) => {
        const recordIDs = Array.from(new Set(items.map((item) => item.finalExaminationID)))

        modalConfirm.setValue({
            isOpen: true,
            headerText: 'Отправка в архив',
            contentText: `Вы действительно хотите отправить в архив все итоговые работы за неделю:<br />👉 ${weekLabel};<br />👉 Количество записей: ${recordIDs.length}.`,
            handleButtonCancel() {modalConfirm.setValue({isOpen: false})},
            async handleButtonOk() {
                modalConfirm.setValue({isOpen: false})
                loadingTitle.setValue('Отправляю в архив...')

                try {
                    const results = await Promise.all(recordIDs.map((recordID) => finalExaminationState.actionSetFinalExaminationInArchive(recordID)))
                    const isSuccess = results.every((res) => res === 200)

                    loadDataListeners(txtSearch.value)

                    if (isSuccess) return notify && notifyOpen('Группа итоговых работ успешно отправлена в архив.', 'success', 1500, notify)
                    return notify && notifyOpen('Часть итоговых работ не удалось отправить в архив.', 'warning', 2500, notify)
                } finally {
                    loadingTitle.setValue('Загрузка итоговых работ...')
                }
            }
        })
    }

    /** Открытие/закрытие анкеты по договору */
    const handleOpenCardContract = (isOpen: boolean, contractID?: number) => {
        cardContract.setValue({
            isOpen, cardMode: 'edit', cardID: contractID,
            handlerButtonCancel() {
                cardContract.setValue({isOpen: false})
                loadDataListeners(txtSearch.value)
            },
            handlerButtonOk() {
                cardContract.setValue({isOpen: false})
                loadDataListeners(txtSearch.value)
            }
        })
    }

    /** Открытие/закрытие анкеты по слушателю */
    const handleOpenCardListener = (isOpen: boolean, ListenerID?: number) => {
        cardListener.setValue({
            isOpen, cardMode: 'edit', cardID: ListenerID,
            handlerButtonCancel() {cardListener.setValue({isOpen: false})}, 
            handlerButtonOk() {
                cardListener.setValue({isOpen: false})
                loadDataListeners(txtSearch.value)
            }
        })
    }
    /** Открытие/закрытие карточки по итоговой работе */
    const handleOpenCardFinalExamination = (isOpen: boolean, finalExaminationID?: number, fullNameFrom?: string, attestationTypeName?: string, dataBase?: string) => {
        cardFinalExamination.setValue({
            isOpen, cardID: finalExaminationID, fullNameFrom, attestationType: attestationTypeName, dataBase,
            handlerButtonCancel() {cardFinalExamination.setValue({isOpen: false})}, 
            handlerButtonOk() {
                cardFinalExamination.setValue({isOpen: false})
                loadDataListeners(txtSearch.value)
            }
        })
    }
    /** Открытие/закрытие окна проверок итоговых работ слушателей */
    const handleOpenFinalExaminationJobs = (isOpen: boolean) => {
        if (countUnverifiedWorks === 0) return notify && notifyOpen('Работы для проверки отсутствуют.', 'info', 1500, notify)

        finalExaminationJobs.setValue({
            isOpen,
            handlerButtonCancel() {finalExaminationJobs.setValue({isOpen: false})}, 
            handlerButtonOk(lastName) {
                txtSearchOnChange(lastName)
                finalExaminationJobs.setValue({isOpen: false})
            }
        })
    }

    const debounceSearch = useDebounce(loadDataListeners, 500) // Запуск загрузки заявок через полсекунды
    const txtSearchOnChange = (value: string) => {txtSearch.setValue(value); debounceSearch(value)} // Поле поиска

    /** Свойства передаваемые в компоненту */
    const propsToComponent: IFinalExamination = {
        isLoading: finalExaminationState.listeners.isLoading,
        txtSearch, txtSearchOnChange, loadDataListeners,

        countUnverifiedWorks: countUnverifiedWorks,

        loadingTitle, currentPageNumber, 
        totalCount: finalExaminationState.listeners.totalCount, 
        pageSize: finalExaminationState.listeners.pageSize,

        listenersList: finalExaminationState.listeners.items,

        handlerOpenModalConfirm, 
    handleArchiveWeek,
        handleOpenCardContract,
        handleOpenCardListener,
        handleOpenCardFinalExamination, 
        handleOpenFinalExaminationJobs,
    }
    
    return <>
        <FinalExamination {...propsToComponent} />

        <ModalConfirm {...modalConfirm.value} />

        <CardListenerContainer {...cardListener.value} />
        <CardContractContainer {...cardContract.value} />
        <CardFinalExaminationContainer {...cardFinalExamination.value} />

        <FinalExaminationJobsContainer {...finalExaminationJobs.value} />
    </>
}